import React, { useCallback, useEffect, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonInputOtp,
} from '@ionic/react';
import { checkmarkCircleOutline, callOutline } from 'ionicons/icons';
import { PhoneLogic } from '@/modules/phone';
import { useUserContext } from '@/modules/user/useUserContext';
import { useAppToast } from '@/hooks/useAppToast';
import { usePhoneVerification } from './usePhoneVerification';
import { MePrFoService } from '@/services/meprfo';
import { apiCall } from '@/util/api';
import { pxToRem } from '@/modules/config/constants';

export type VerificationModalCloseReason = 'cancelled' | 'expired' | 'success';

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: (reason: VerificationModalCloseReason) => void;
  onSuccess?: () => void;
  continuePath: string;
  // When the user is changing their number, the previous number. Forwarded to
  // verification so the backend can text it a change notice on success.
  previousPhone?: string;
  // When the user is migrating to a new number, its client-computed OPRF + tail.
  // Forwarded to initiation so the backend can move the profile onto the new
  // number once it's verified (see usePhoneVerification).
  migrate?: { phoneOprf: string; phoneTail: string };
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  continuePath: providedContinuePath,
  previousPhone,
  migrate,
}) => {
  const { userId, phone: contextPhone, refreshUserContext } = useUserContext();

  const [presentToast, dismissToast] = useAppToast();
  const [isPreparing, setIsPreparing] = useState(false);

  // Replace any visible toast before presenting the next one. A single
  // useAppToast instance shows one toast at a time, so without this a rapid
  // success→error sequence (e.g. "code sent" then an invalid code) would have
  // the second toast swallowed by the first's lingering overlay.
  const showToast = useCallback(
    (message: string, color: 'success' | 'danger'): void => {
      void dismissToast().then(() =>
        presentToast({ message, color, duration: 3000 })
      );
    },
    [presentToast, dismissToast]
  );

  const { status, code, setCode, sendSms, confirmCode, resend, reset } =
    usePhoneVerification({
      phone: contextPhone ?? null,
      continuePath: providedContinuePath,
      previousPhone,
      migrate,
      onSuccess: () => {
        onClose('success');
        onSuccess?.();
      },
      onExpired: () => {
        handleClose('expired');
      },
      onInvalidCode: message => {
        showToast(message, 'danger');
      },
    });

  const handleClose = (
    reason: VerificationModalCloseReason = 'cancelled'
  ): void => {
    dismissToast();
    reset();
    onClose(reason);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!contextPhone) {
      showToast('Phone number is required for phone verification', 'danger');
      return;
    }

    setIsPreparing(true);
    try {
      // Make sure the active session belongs to the number we're about to
      // verify. This covers two cases: (1) no session at all (e.g. profile was
      // reset but the phone is still in local storage), and (2) a stale session
      // left over from a different number — e.g. correcting a mistyped number,
      // where the throwaway account's phone_tail no longer matches and the
      // backend would otherwise reject the SMS with a phone_mismatch.
      let existingUserId: string | null = null;
      try {
        const lookup = await MePrFoService.checkPhoneExists(contextPhone.value);
        existingUserId = lookup.userId;
      } catch (lookupError) {
        // If we can't resolve the owner but already have a session, let the
        // verification request itself be the source of truth rather than
        // blocking a send on a transient OPRF failure.
        if (!userId) throw lookupError;
      }

      if (existingUserId && existingUserId !== userId) {
        const identifyResponse = await apiCall('/api/auth/identify', {
          method: 'POST',
          body: JSON.stringify({ id: existingUserId }),
        });
        if (!identifyResponse.ok) {
          throw new Error(
            'Could not re-establish your session. Please complete your profile first.'
          );
        }
        await refreshUserContext();
      } else if (!existingUserId && !userId) {
        throw new Error(
          'Could not re-establish your session. Please complete your profile first.'
        );
      }

      await sendSms();

      showToast(
        'Verification SMS sent! Please enter the 6-digit code from your SMS.',
        'success'
      );
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'An error occurred',
        'danger'
      );
    } finally {
      setIsPreparing(false);
    }
  };

  // Reset hook state when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      reset();
      dismissToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onSendPage = status === 'idle' || status === 'sending';
  const submitting = isPreparing || status === 'sending';
  const submittingCode = status === 'verifying';

  return (
    <IonModal isOpen={isOpen} onDidDismiss={() => handleClose('cancelled')}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Phone Verification</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {onSendPage && (
          <div style={{ padding: '20px' }}>
            <IonText>
              <p style={{ fontSize: pxToRem(14) }}>
                We need to send you an SMS to verify your phone number.
              </p>
            </IonText>
            <IonItem>
              <IonLabel position="stacked">
                <IonIcon icon={callOutline} style={{ marginRight: '5px' }} />
                Phone Number
              </IonLabel>
              <IonText>
                <p style={{ margin: '8px 0', fontSize: pxToRem(16) }}>
                  {contextPhone
                    ? PhoneLogic.format(contextPhone.value)
                    : 'No phone number set'}
                </p>
              </IonText>
            </IonItem>

            <div
              style={{
                padding: '20px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <IonButton expand="block" onClick={handleSubmit} color="primary">
                {submitting ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <IonIcon icon={checkmarkCircleOutline} />
                )}
                {submitting ? 'Processing...' : 'Send SMS'}
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => handleClose('cancelled')}
                color="medium"
              >
                Cancel
              </IonButton>
            </div>
          </div>
        )}

        {!onSendPage && (
          <div style={{ padding: '20px' }}>
            <IonText>
              <p style={{ fontSize: pxToRem(14), marginBottom: '20px' }}>
                Enter the 6-digit verification code sent to your phone.
              </p>
            </IonText>

            <div
              style={{
                margin: '20px 0',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <IonInputOtp
                length={6}
                separators={[3]}
                value={code}
                onIonInput={e => {
                  const value = e.detail.value || '';
                  const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
                  setCode(digitsOnly);
                }}
                disabled={submittingCode}
              />
            </div>

            <div
              style={{
                padding: '20px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <IonButton
                expand="block"
                onClick={() => confirmCode()}
                color="success"
                disabled={code.length !== 6 || submittingCode}
              >
                {submittingCode ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <IonIcon icon={checkmarkCircleOutline} />
                )}
                {submittingCode ? 'Verifying...' : 'Verify Code'}
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => resend()}
                color="secondary"
              >
                Resend SMS
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => handleClose('cancelled')}
                color="medium"
              >
                Cancel
              </IonButton>
            </div>

            <IonText>
              <p
                style={{
                  fontSize: pxToRem(12),
                  color: '#666',
                  marginTop: '20px',
                  textAlign: 'center',
                }}
              >
                Your SMS also includes a confirmation link that can be used
                instead of entering the code here.
              </p>
            </IonText>
          </div>
        )}

        <div style={{ padding: '0 20px 20px', marginTop: 'auto' }}>
          <IonText color="medium">
            <p style={{ fontSize: pxToRem(14), margin: 0 }}>
              By providing your number, you agree to receive a one-time SMS from
              Snowball. Message/data rates apply.
            </p>
          </IonText>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default PhoneVerificationModal;
